export interface Employee {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Sales Executive' | 'Customer Service';
  department: string;
  avatar?: string;
  status: 'Active' | 'Inactive' | 'On Leave';
  joinDate: string;
  lastActive: string;
  performance: {
    totalQuotations: number;
    wonQuotations: number;
    lostQuotations: number;
    pendingQuotations: number;
    avgResponseTime: number; // in hours
    winRate: number; // percentage
    totalRevenue: number;
    avgDealValue: number;
    customerSatisfaction: number; // 1-5 rating
    monthlyTarget: number;
    monthlyAchievement: number;
  };
  currentWorkload: {
    activeQuotations: number;
    overdueQuotations: number;
    todayTasks: number;
    thisWeekTasks: number;
  };
  skills: string[];
  certifications: string[];
}

export interface TeamPerformance {
  teamName: string;
  totalMembers: number;
  activeMembers: number;
  totalQuotations: number;
  wonQuotations: number;
  avgResponseTime: number;
  winRate: number;
  totalRevenue: number;
  monthlyTarget: number;
  monthlyAchievement: number;
}

export const dummyEmployees: Employee[] = [
  {
    id: 'EMP-001',
    name: 'Prashant Pathil',
    email: 'prashant@galific.com',
    role: 'Manager',
    department: 'Sales',
    avatar: '👨‍💼',
    status: 'Active',
    joinDate: '2023-01-15',
    lastActive: '2025-01-15T10:30:00',
    performance: {
      totalQuotations: 156,
      wonQuotations: 89,
      lostQuotations: 45,
      pendingQuotations: 22,
      avgResponseTime: 2.3,
      winRate: 66.4,
      totalRevenue: 2450000,
      avgDealValue: 27528,
      customerSatisfaction: 4.7,
      monthlyTarget: 25,
      monthlyAchievement: 18
    },
    currentWorkload: {
      activeQuotations: 12,
      overdueQuotations: 2,
      todayTasks: 8,
      thisWeekTasks: 24
    },
    skills: ['Air Freight', 'Ocean Freight', 'Customer Relations', 'Pricing Strategy'],
    certifications: ['IATA Certified', 'Customs Broker License']
  },
  {
    id: 'EMP-002',
    name: 'Sarah Johnson',
    email: 'sarah@galific.com',
    role: 'Sales Executive',
    department: 'Sales',
    avatar: '👩‍💼',
    status: 'Active',
    joinDate: '2023-03-20',
    lastActive: '2025-01-15T09:45:00',
    performance: {
      totalQuotations: 98,
      wonQuotations: 52,
      lostQuotations: 32,
      pendingQuotations: 14,
      avgResponseTime: 3.1,
      winRate: 61.9,
      totalRevenue: 1280000,
      avgDealValue: 24615,
      customerSatisfaction: 4.5,
      monthlyTarget: 20,
      monthlyAchievement: 15
    },
    currentWorkload: {
      activeQuotations: 8,
      overdueQuotations: 1,
      todayTasks: 6,
      thisWeekTasks: 18
    },
    skills: ['Express Delivery', 'Road Transport', 'Customer Service', 'Lead Generation'],
    certifications: ['Logistics Management', 'Customer Service Excellence']
  },
  {
    id: 'EMP-003',
    name: 'Mike Chen',
    email: 'mike@galific.com',
    role: 'Sales Executive',
    department: 'Sales',
    avatar: '👨‍💻',
    status: 'Active',
    joinDate: '2023-06-10',
    lastActive: '2025-01-15T11:15:00',
    performance: {
      totalQuotations: 87,
      wonQuotations: 41,
      lostQuotations: 28,
      pendingQuotations: 18,
      avgResponseTime: 4.2,
      winRate: 59.4,
      totalRevenue: 980000,
      avgDealValue: 23902,
      customerSatisfaction: 4.3,
      monthlyTarget: 18,
      monthlyAchievement: 12
    },
    currentWorkload: {
      activeQuotations: 10,
      overdueQuotations: 3,
      todayTasks: 7,
      thisWeekTasks: 21
    },
    skills: ['Ocean Freight', 'Warehousing', 'Documentation', 'Compliance'],
    certifications: ['Maritime Logistics', 'Dangerous Goods Handling']
  },
  {
    id: 'EMP-004',
    name: 'Lisa Brown',
    email: 'lisa@galific.com',
    role: 'Customer Service',
    department: 'Support',
    avatar: '👩‍🎓',
    status: 'Active',
    joinDate: '2023-08-05',
    lastActive: '2025-01-15T08:20:00',
    performance: {
      totalQuotations: 45,
      wonQuotations: 28,
      lostQuotations: 12,
      pendingQuotations: 5,
      avgResponseTime: 1.8,
      winRate: 70.0,
      totalRevenue: 420000,
      avgDealValue: 15000,
      customerSatisfaction: 4.8,
      monthlyTarget: 12,
      monthlyAchievement: 9
    },
    currentWorkload: {
      activeQuotations: 4,
      overdueQuotations: 0,
      todayTasks: 5,
      thisWeekTasks: 15
    },
    skills: ['Customer Support', 'Problem Solving', 'Communication', 'Follow-up'],
    certifications: ['Customer Service Excellence', 'Communication Skills']
  },
  {
    id: 'EMP-005',
    name: 'David Wilson',
    email: 'david@galific.com',
    role: 'Sales Executive',
    department: 'Sales',
    avatar: '👨‍🔧',
    status: 'On Leave',
    joinDate: '2023-02-28',
    lastActive: '2025-01-10T16:30:00',
    performance: {
      totalQuotations: 124,
      wonQuotations: 67,
      lostQuotations: 38,
      pendingQuotations: 19,
      avgResponseTime: 2.8,
      winRate: 63.8,
      totalRevenue: 1680000,
      avgDealValue: 25075,
      customerSatisfaction: 4.6,
      monthlyTarget: 22,
      monthlyAchievement: 16
    },
    currentWorkload: {
      activeQuotations: 0,
      overdueQuotations: 0,
      todayTasks: 0,
      thisWeekTasks: 0
    },
    skills: ['Air Freight', 'Express Delivery', 'International Trade', 'Negotiation'],
    certifications: ['IATA Certified', 'International Trade Law']
  },
  {
    id: 'EMP-006',
    name: 'Emma Davis',
    email: 'emma@galific.com',
    role: 'Admin',
    department: 'Administration',
    avatar: '👩‍💻',
    status: 'Active',
    joinDate: '2022-11-15',
    lastActive: '2025-01-15T12:00:00',
    performance: {
      totalQuotations: 0,
      wonQuotations: 0,
      lostQuotations: 0,
      pendingQuotations: 0,
      avgResponseTime: 0,
      winRate: 0,
      totalRevenue: 0,
      avgDealValue: 0,
      customerSatisfaction: 4.9,
      monthlyTarget: 0,
      monthlyAchievement: 0
    },
    currentWorkload: {
      activeQuotations: 0,
      overdueQuotations: 0,
      todayTasks: 12,
      thisWeekTasks: 35
    },
    skills: ['System Administration', 'Data Management', 'Reporting', 'Process Optimization'],
    certifications: ['Project Management', 'Data Analytics', 'System Administration']
  }
];

export const teamPerformance: TeamPerformance[] = [
  {
    teamName: 'Sales Team',
    totalMembers: 4,
    activeMembers: 3,
    totalQuotations: 465,
    wonQuotations: 249,
    avgResponseTime: 3.1,
    winRate: 63.5,
    totalRevenue: 6390000,
    monthlyTarget: 85,
    monthlyAchievement: 61
  },
  {
    teamName: 'Support Team',
    totalMembers: 1,
    activeMembers: 1,
    totalQuotations: 45,
    wonQuotations: 28,
    avgResponseTime: 1.8,
    winRate: 70.0,
    totalRevenue: 420000,
    monthlyTarget: 12,
    monthlyAchievement: 9
  },
  {
    teamName: 'Administration',
    totalMembers: 1,
    activeMembers: 1,
    totalQuotations: 0,
    wonQuotations: 0,
    avgResponseTime: 0,
    winRate: 0,
    totalRevenue: 0,
    monthlyTarget: 0,
    monthlyAchievement: 0
  }
];
